import axios from './axios';
import { UserAccountType, UserSubscriptionType } from '../types/auth';

// 用户接口定义（与后端实体保持一致）
export interface User {
  userID: string;
  userName: string;
  userPhoneCountryCode?: string;
  userPhone?: string;
  userEmail?: string;
  userPassword?: string;
  userRealName?: string;
  userNickName?: string;
  userAvatar?: string;
  userPosition?: string;
  userRole?: string; // 多选，逗号分隔
  userCountry?: string;
  userCity?: string;
  userAccountType?: UserAccountType;
  userAccountStatus?: string;
  
  // 订阅相关字段 - 分场排期产品
  userSubscriptionBreakdownVersion?: UserSubscriptionType;
  userSubscriptionBreakdownStartDate?: Date | string;
  userSubscriptionBreakdownExpirationDate?: Date | string;
  
  // 订阅相关字段 - 预算管理产品
  userSubscriptionBudgetsVersion?: UserSubscriptionType;
  userSubscriptionBudgetsStartDate?: Date | string;
  userSubscriptionBudgetsExpirationDate?: Date | string;
  
  // 权限相关字段
  userManagementOrgs?: string; // 多个，逗号分隔
  userManagementDepartments?: string; // 多个，逗号分隔
  
  // 组织相关字段
  userOrgID?: string; // 多个，逗号分隔
  
  userAppConfig?: string;

  // 用户行为记忆相关字段
  userLastUsedFilmID?: string;
  userLastUsedBudgetID?: string;
  userLastUsedBudgetItemID?: string; // 存储多个 [budgetID;budgetItemID] 的数组，使用JSON格式
  userLastUsedBreakdownID?: string;
  userLastUsedSceneID?: string; // 存储多个 [breakdownID;sceneID] 的数组，使用JSON格式
  userLastUsedElementID?: string; // 存储多个 [breakdownID;elementID] 的数组，使用JSON格式
  userLastUsedScheduleID?: string; // 存储多个 [breakdownID;scheduleID] 的数组，使用JSON格式
  userLastUsedSlotID?: string; // 存储多个 [breakdownID;scheduleID;slotID] 的数组，使用JSON格式
  userLastUsedCalDateID?: string; // 存储多个 [scheduleID;calDateID] 的数组，使用JSON格式
  userLastUsedCalendarDate?: string; // 存储多个 [scheduleID;日期] 的数组，使用JSON格式
  
  createdAt: Date | string;
  updatedAt: Date | string;
}

// API 响应类型
export interface UserResponse {
  data: User;
  status: number;
  statusText: string;
}

export interface UsersResponse {
  data: User[];
  status: number;
  statusText: string;
}

export const userCache = new Map<string, User>();

export const getUserInfoWithCache = async (userId: string): Promise<User | null> => {
  if (!userId) return null;
  if (userCache.has(userId)) return userCache.get(userId)!;
  const res = await getUserById(userId);
  if (res.data) userCache.set(userId, res.data);
  return res.data || null;
};

// 获取单个用户信息
export const getUserById = (userID: string): Promise<UserResponse> => {
  return axios.get<User>(`/api/users/${userID}`);
};

// 获取用户列表
export const getUsers = (params?: {
  keyword?: string;
  limit?: number;
  exact?: 'true' | 'false';
  userName?: string;
  userRole?: string;
  userOrgID?: string;
  userPhoneCountryCode?: string;
  userPhone?: string;
  userEmail?: string;
  userAccountType?: UserAccountType;
  userAccountStatus?: string;
  userSubscriptionBreakdownVersion?: UserSubscriptionType;
  userSubscriptionBudgetsVersion?: UserSubscriptionType;
}): Promise<UsersResponse> => {
  return axios.get<User[]>('/api/users', { params });
};

// 按角色查询用户
export const getUsersByRole = (roleName: string): Promise<UsersResponse> => {
  return axios.get<User[]>(`/api/users/role/${roleName}`);
};

// 批量获取用户信息
export const getUsersByIds = (userIds: string[]): Promise<UsersResponse> => {
  // 后端接口为 GET /api/users/batch?ids=xxx,yyy,zzz
  return axios.get<User[]>(`/api/users/batch`, { params: { ids: userIds.join(',') } });
};

// 更新用户信息
export const updateUser = (userID: string, updateData: Partial<User>): Promise<UserResponse> => {
  return axios.put<User>(`/api/users/${userID}`, updateData);
};
